import { AnswerOptionCondition } from 'shared/types/due-diligence';
import { getConditionLabel } from './condition-labels';
import { CONDITION_TYPES } from './condition-utils';
import {
  File,
  Calendar,
  ListChecks,
  Robot,
  MagnifyingGlass,
  Database,
  GitBranch
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface NaturalLanguageSentenceProps {
  condition: AnswerOptionCondition;
  isEditing: boolean;
  onClick: () => void;
}

const iconMap = {
  File,
  Calendar,
  ListChecks,
  Robot,
  MagnifyingGlass,
  Database,
  GitBranch,
};

const parseMarkdownBold = (text: string): React.ReactNode[] => {
  const parts = text.split(/(\*\*.*?\*\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={index} className="font-semibold text-gray-900">{boldText}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

export const NaturalLanguageSentence: React.FC<NaturalLanguageSentenceProps> = ({
  condition,
  isEditing,
  onClick,
}) => {
  const label = getConditionLabel(condition);
  const conditionTypeConfig = CONDITION_TYPES.find((ct) => ct.type === condition.type);

  const IconComponent = conditionTypeConfig?.iconName
    ? iconMap[conditionTypeConfig.iconName as keyof typeof iconMap]
    : File;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-start gap-3 p-3 rounded-lg text-left transition-all',
        'border-2',
        isEditing
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      )}
    >
      <div className="flex-shrink-0 mt-0.5">
        {IconComponent && (
          <IconComponent
            size={20}
            weight="regular"
            className={cn(
              'transition-colors',
              isEditing ? 'text-blue-600' : 'text-gray-500'
            )}
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-700 leading-relaxed">
          {parseMarkdownBold(label)}
        </div>
      </div>
    </button>
  );
};
